import { Injectable } from '@angular/core';
import { BaseApiService, Result, ResultType, aaDataResponse } from '@shared/data-access';
import { BaseListOfficerService, OfficerBaseResponse, OfficerForOrganizationChart, OfficerResponse } from '@shared/feature/list-officer';
import { TableData, TableDataResponse } from '@shared/ui/tables';
import { Observable, first, map, of, switchMap, throwError } from 'rxjs';
import { IntrensicOfficerResponse, OfficerDetailFacade } from '@intrensic/shared/officer-details';
import { UserRole } from '@shared/utils/base-auth';
import { TranslationFromJsonService } from '@shared/feature/translate';

@Injectable({
  providedIn: 'root',
})
export class IntrensicListOfficersService extends BaseListOfficerService {
  constructor(api: BaseApiService, private currentOfficerFacade: OfficerDetailFacade, private translationService: TranslationFromJsonService) {
    super(api);
  }

  override list(query: string): Observable<Result<TableDataResponse<OfficerResponse>>> {
    return this.currentOfficerFacade.getLoggedInOfficer().pipe(
      first(),
      switchMap((loggedInOfficer) => {
        const role = loggedInOfficer.role;

        switch (role) {
          case UserRole.Supervisor:
          case UserRole.Commander:
          case UserRole.HRAdmin:
          case UserRole.SuperAdmin:
            return this.api.get<aaDataResponse<IntrensicOfficerResponse>>(`api/users/odata?${query}`).pipe(
              map((response) => {
                const tableData = new TableData(
                  response.iTotalRecords,
                  response.aaData.map(
                    (values) =>
                      new OfficerResponse(
                        values.UID,
                        values.FirstName,
                        values.LastName,
                        null,
                        values.EmailAddress,
                        values.TimeZone,
                        values.EmployeeID,
                        values.PictureFullPath,
                        values.InviteSent,
                        values.AccountType,
                        values.Phone,
                        values.Role,
                        values.ShareType,
                        values.RespondTo != null
                          ? new OfficerBaseResponse(
                              values.RespondTo.UID,
                              values.RespondTo.FirstName,
                              values.RespondTo.LastName,
                              null,
                              values.RespondTo.EmailAddress,
                              values.RespondTo.TimeZone,
                              values.RespondTo.EmployeeID,
                              values.RespondTo.PictureFullPath,
                              values.InviteSent,
                              values.AccountType
                            )
                          : null,
                        values.Children
                      )
                  )
                );

                return {
                  value: tableData,
                  isSuccess: true,
                  isFailure: false,
                  isEmpty: false,
                  message: '',
                  httpStatusCode: 200,
                  resultType: ResultType.Ok,
                };
              })
            );

          default:
            return of({
              value: new TableData(0, []),
              isSuccess: true,
              isFailure: false,
              isEmpty: false,
              message: '',
              httpStatusCode: 200,
              resultType: ResultType.Ok,
            });
        }
      })
    );
  }

  override deleteOfficer(uid: string): Observable<string> {
    return this.api.delete(`api/users/${uid}`);
  }

  override inviteUser(uid: string): Observable<string> {
    return this.api.post(`api/users/${uid}/invite`);
  }

  override listOrgChartOfficers(): Observable<Result<OfficerForOrganizationChart[]>> {
    return this.currentOfficerFacade.getLoggedInOfficer().pipe(
      first(),
      switchMap((loggedInOfficer) => {
        const role = loggedInOfficer.role;

        switch (role) {
          case UserRole.HRAdmin:
          case UserRole.SuperAdmin:
            return this.api.get<aaDataResponse<IntrensicOfficerResponse>>(`api/users/odata`).pipe(
              map((response) => {
                const orgChartOfficers = response.aaData.map(
                  (r) => new OfficerForOrganizationChart(r.UID, r.FirstName, r.LastName, r.PictureFullPath, r.RespondTo?.UID ?? null, r.Children != null, r.EmailAddress, r.Role)
                );

                return {
                  value: orgChartOfficers,
                  isSuccess: true,
                  isFailure: false,
                  isEmpty: orgChartOfficers.length === 0,
                  message: '',
                  httpStatusCode: 200,
                  resultType: ResultType.Ok,
                } as Result<OfficerForOrganizationChart[]>;
              })
            );

          default:
            return throwError(() => new Error(this.translationService.instant('user-role-not-authorized')));
        }
      })
    );
  }
}
